import { useAuth0 } from '@auth0/auth0-react';
import { WarningsContext } from '@common/context/warningsContext';
import Config from '@config';
import IconButton from '@ds/components/actions/IconButton';
import Alert from '@ds/components/feedback/Alert';
import Box from '@ds/components/layout/Box';
import { BP_DESKTOP } from '@ds/foundations/tokens';
import { MenuIcon } from '@ds/icons/generated';
import useUserDetails from '@effects/useUserdetails';
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Offline } from 'react-detect-offline';
import { useMediaQuery } from 'react-responsive';

import Brand from './Brand';
import styles from './Header.module.css';
import NavigationBar from './NavigationBar';

/** The custom property on <html> that Header and PageLayout read for the banner stack's height. */
const BANNERS_HEIGHT_PROPERTY = '--header-banners-height';

/** How long the error banner stays up after a page raises it. */
const WARNING_DURATION_MS = 2500;

/**
 * Publishes the banner stack's rendered height on <html> while the Header is fixed, so the page pushes its content
 * below the banners and keeps a focused control clear of them (WCAG 2.4.11, ENVDB-9286 item 81). The stack is empty
 * (0px) when no banner shows and grows when one wraps or the text is enlarged, so it is measured rather than assumed.
 * CSS decides how the value applies.
 */
const usePublishBannersHeight = (bannersRef: React.RefObject<HTMLElement>, enabled: boolean): void => {
    useLayoutEffect(() => {
        const banners = bannersRef.current;
        if (!enabled || !banners) return undefined;

        const root = document.documentElement;
        const publish = (): void => {
            root.style.setProperty(BANNERS_HEIGHT_PROPERTY, `${Math.ceil(banners.getBoundingClientRect().height)}px`);
        };

        publish();
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(publish);
        observer?.observe(banners);

        return () => {
            observer?.disconnect();
            root.style.removeProperty(BANNERS_HEIGHT_PROPERTY);
        };
    }, [bannersRef, enabled]);
};

interface HeaderProps {
    /**
     * Fixes the header to the top of the viewport, with the warning banners stacked below it. Below 600px tall it
     * sits in the page flow instead and only the banners stick. `false` renders both in flow (Storybook).
     * @default true
     */
    sticky?: boolean;
}

const Header: React.FC<HeaderProps> = ({ sticky = true }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const { isAuthenticated } = useAuth0();
    const { isLoading } = useUserDetails();
    const [showWarning, setShowWarning] = useContext(WarningsContext);
    const isTabletOrMobile = useMediaQuery({ maxWidth: BP_DESKTOP - 1 });

    const bannersRef = useRef<HTMLDivElement>(null);
    usePublishBannersHeight(bannersRef, sticky);

    const isLoggedIn = Boolean(isAuthenticated && !isLoading);

    useEffect(() => {
        if (!showWarning) return undefined;
        const timer = setTimeout(() => setShowWarning(false), WARNING_DURATION_MS);
        return () => clearTimeout(timer);
    }, [showWarning, setShowWarning]);

    return (
        <>
            <header
                className={styles.header}
                data-sticky={sticky}
            >
                <div className={styles.inner}>
                    {isLoggedIn && isTabletOrMobile && (
                        <Box pr={6}>
                            <IconButton
                                icon={<MenuIcon />}
                                label="Menu"
                                variant="text"
                                shape="circle"
                                onClick={() => setMenuOpen(true)}
                                className={styles.menuButton}
                            />
                        </Box>
                    )}
                    {!isTabletOrMobile && <Brand />}
                    <NavigationBar
                        menuOpen={menuOpen}
                        closeAction={() => setMenuOpen(false)}
                    />
                </div>
            </header>
            {/* One stack, so the two banners never overlap; empty (0px) when neither shows. */}
            <div
                ref={bannersRef}
                className={styles.banners}
                data-sticky={sticky}
                data-header-banners
            >
                {Config.FLAG_OFFLINE_CAPABLE && (
                    <Offline polling={{ url: Config.POLLING_URL, interval: Config.POLLING_INTERVAL, enabled: true }}>
                        <Alert
                            compact
                            variant="danger"
                            title="You are currently offline, changes may not be saved"
                            className={styles.warning}
                            emphasis="solid"
                        />
                    </Offline>
                )}
                {showWarning && (
                    <Alert
                        compact
                        variant="danger"
                        title="An error occurred, changes may not be saved."
                        className={styles.warning}
                        emphasis="solid"
                    />
                )}
            </div>
        </>
    );
};

export default Header;
